import React from 'react';
import { Category } from '../api/game';

interface CategoryCardProps {
  category: Category;
  onPlay: (categoryId: string) => void;
  disabled?: boolean;
}

const difficultyLabel = (difficulty: number): string => {
  switch (difficulty) {
    case 1: return 'Easy';
    case 2: return 'Medium';
    case 3: return 'Hard';
    default: return 'Easy';
  }
};

const difficultyColor = (difficulty: number): string => {
  switch (difficulty) {
    case 1: return '#4CAF50';  // Green
    case 2: return '#FF9800';  // Orange
    case 3: return '#f44336';  // Red
    default: return '#4CAF50';
  }
};

// Render trophy icons for perfect scores
const TrophyIcons: React.FC<{ count: number; max: number }> = ({ count, max }) => {
  const trophies = [];
  for (let i = 0; i < max; i++) {
    const earned = i < count;
    trophies.push(
      <img
        key={i}
        src="/avatars/trophy.svg"
        alt={earned ? 'Trophy earned' : 'Trophy not earned'}
        style={{
          width: '16px',
          height: '16px',
          opacity: earned ? 1 : 0.2,
          marginRight: '2px'
        }}
      />
    );
  }
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>{trophies}</div>;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onPlay, disabled }) => {
  const difficulty = category.difficulty || 1;
  const level = category.level || 1;
  const isMastered = category.is_mastered || false;
  const isLocked = !category.is_unlocked;
  const perfectCount = category.perfect_count || 0;
  const requiredPerfect = category.required_perfect_count || 5;
  const masteryThreshold = category.mastery_threshold || 10;

  return (
    <div className={`category-card ${isLocked ? 'category-locked' : ''} ${isMastered ? 'category-mastered' : ''}`}>
      <div className="category-header">
        <div className="category-name">
          {isMastered && <span className="mastery-icon">&#127942; </span>}
          {isLocked && !isMastered && <span className="lock-icon">&#128274; </span>}
          {category.name}
        </div>
        <div className="category-level" style={{
          fontSize: '0.8em',
          color: isMastered ? '#FFD700' : '#888',
          fontWeight: 'bold'
        }}>
          Level {level}
        </div>
      </div>
      <div
        className="category-difficulty"
        style={{
          color: difficultyColor(difficulty),
          fontWeight: 'bold',
          fontSize: '0.9em',
          marginBottom: '4px'
        }}
      >
        {difficultyLabel(difficulty)}
      </div>
      {category.description && (
        <div className="category-description">{category.description}</div>
      )}
      <div className="category-info">
        <span className="category-stat">{category.card_count} cards</span>
        <span className="category-stat">{category.time_limit_seconds}s per question</span>
      </div>
      {!isMastered && (
        <div className="category-rewards" style={{
          fontSize: '0.85em',
          color: '#666',
          marginTop: '4px',
          marginBottom: '8px'
        }}>
          Earn {level} XP & {level} coin per correct answer
        </div>
      )}
      {isMastered ? (
        <div className="category-mastered-msg" style={{
          fontSize: '0.9em',
          color: '#FFD700',
          fontWeight: 'bold',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          <TrophyIcons count={masteryThreshold} max={masteryThreshold} />
          <div style={{ marginTop: '4px' }}>MASTERED!</div>
        </div>
      ) : isLocked ? (
        <div className="category-locked-msg" style={{
          fontSize: '0.85em',
          color: '#999',
          fontStyle: 'italic',
          marginBottom: '8px'
        }}>
          Complete previous level with {requiredPerfect} perfect scores to unlock
        </div>
      ) : (
        <div className="category-progress" style={{
          fontSize: '0.85em',
          color: perfectCount >= requiredPerfect ? '#4CAF50' : '#666',
          marginBottom: '8px'
        }}>
          <TrophyIcons count={perfectCount} max={masteryThreshold} />
          <div style={{ marginTop: '4px' }}>
            {perfectCount}/{masteryThreshold} perfect scores
            {perfectCount >= requiredPerfect && perfectCount < masteryThreshold && ' (Next level unlocked!)'}
          </div>
        </div>
      )}
      <button
        className="category-btn"
        onClick={() => onPlay(category.id)}
        disabled={disabled || isLocked || isMastered}
      >
        {isMastered ? 'Mastered' : isLocked ? 'Locked' : 'Play'}
      </button>
    </div>
  );
};

export default CategoryCard;
